import {
  compareColumns,
  compareRows,
  startAddress,
} from "@/jspreadsheet/utils";

export function tagKey(prefix: string, name: string) {
  return `${prefix}_${name}`;
}

/**
 * DB 태그 키(`prefix_name`)를 나눈다. prefix 에는 `_` 가 없고 name 에는 있을 수 있어 첫 `_` 로만 자른다.
 * `_` 가 없으면 name 은 undefined.
 */
export function destructTagKey(tagKey: string): {
  prefix: string;
  name: string | undefined;
} {
  const index = tagKey.indexOf("_");
  if (index < 0) return { prefix: tagKey, name: undefined };
  return { prefix: tagKey.slice(0, index), name: tagKey.slice(index + 1) };
}

export function tagKeyWithRole(prefix: string, name: string, role: string) {
  return `${role}_${prefix}_${name}`;
}

const parseRoleKey = (text: string): string => {
  const matches = text.match(/\[(.*?)\]/);
  return matches ? matches[1] : "";
};
// 헬퍼 함수: 셀이 범위 안에 포함되는지 확인
export const isCellInRange = (
  cell: string,
  rangeStart: string,
  rangeEnd: string,
): boolean => {
  const cellCol = compareColumns(cell, rangeStart);
  const cellRow = compareRows(cell, rangeStart);
  const cellToEndCol = compareColumns(cell, rangeEnd);
  const cellToEndRow = compareRows(cell, rangeEnd);

  // 셀이 범위의 시작점보다 크거나 같고, 끝점보다 작거나 같아야 함
  return cellCol >= 0 && cellRow >= 0 && cellToEndCol <= 0 && cellToEndRow <= 0;
};

/**
 * 셀 목록에서 주소가 가리키는 셀을 찾는다.
 *
 * 머지 셀은 목록에 `"D10:D12"` 같은 범위 주소로 들어오는데, 선택 상태가 들고 있는
 * 주소는 앵커(`"D10"`)일 수 있다. 그래서 문자열 완전일치 대신 포함 검사로 매칭한다.
 */
export const findCellByAddress = <T extends { address: string }>(
  cells: T[] | undefined,
  address: string | null | undefined,
): T | undefined => {
  if (!cells || !address) return undefined;

  const anchor = startAddress(address);
  return cells.find((cell) => {
    const [cellStart, cellEnd = cellStart] = cell.address.split(":");
    return isCellInRange(anchor, cellStart, cellEnd);
  });
};

// 헬퍼 함수: 범위가 다른 범위와 겹치는지 확인
export const isRangeOverlapping = (
  range1Start: string,
  range1End: string,
  range2Start: string,
  range2End: string,
): boolean => {
  // 범위1의 끝이 범위2의 시작보다 앞에 있거나, 범위1의 시작이 범위2의 끝보다 뒤에 있으면 겹치지 않음
  const range1EndBeforeRange2Start =
    compareColumns(range1End, range2Start) < 0 ||
    compareRows(range1End, range2Start) < 0;
  const range1StartAfterRange2End =
    compareColumns(range1Start, range2End) > 0 ||
    compareRows(range1Start, range2End) > 0;

  return !(range1EndBeforeRange2Start || range1StartAfterRange2End);
};
